import { faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { useNavigate, useSearchParams } from "react-router-dom";

import { T, useT } from "@repo/transifex";
import { Button } from "~/components";
import { useOpenCashDrawer } from "~/hooks";
import { useOrderCancel } from "~/hooks/queries/orders";
import { usePaymentCreate } from "~/hooks/queries/payments";
import { useCashDrawerStatus, useDeviceData } from "~/providers/app";
import { useSkipReceipt } from "~/hooks/use-skip-receipt";

type CashResultProps = {
  orderId: string | null;
  change: number;
  loadingOrder: boolean;
  received: number;
};

export const CalculatorButtons = ({
  orderId,
  change,
  loadingOrder,
  received
}: CashResultProps) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const t = useT();

  const openCashDrawer = useOpenCashDrawer();
  const { os, osVersion } = useDeviceData();
  const { mutate: payOrder, status } = usePaymentCreate();
  const { mutate: cancelOrder, status: cancelStatus } = useOrderCancel();

  const cashDrawerStatus = useCashDrawerStatus();
  const isPositiveChange = change >= 0;

  const skipReceipt = useSkipReceipt();

  const loading =
    status === "pending" || loadingOrder || cancelStatus === "pending";

  return (
    <div className="flex w-full gap-3 p-5">
      <Button
        disabled={loading}
        onClick={() => {
          if (orderId) {
            cancelOrder(orderId);
            navigate("..");
          }
        }}
        size="xl"
        variant="danger"
        className="flex-1"
      >
        <T _str="Cancel" />
      </Button>
      <Button
        className="flex-1"
        disabled={
          cashDrawerStatus !== "closed" || loadingOrder || received === 0
        }
        icon={loading ? faSpinner : undefined}
        loading={loading}
        loadingText={t("Processing order")}
        onClick={() => {
          if (orderId && os && osVersion)
            payOrder(
              {
                change: isPositiveChange ? change : 0, // if change is negative -> split payment
                orderId,
                paymentProvider: "cash",
                deviceInfo: {
                  os,
                  osVersion
                },
                // Math.round: `received` is major units; `* 100` alone leaks IEEE-754
                // drift (e.g. 1.15 * 100 = 114.99999999999999)
                unitCashReceived: Math.round(received * 100)
              },
              {
                onSuccess: () => {
                  openCashDrawer("automatic");

                  if (!isPositiveChange) {
                    navigate("..");
                    setSearchParams({
                      orderId
                    });

                    return;
                  }

                  skipReceipt(orderId);
                }
              }
            );
        }}
        size="xl"
      >
        {isPositiveChange ? <T _str="Confirm" /> : <T _str="Split payment" />}
      </Button>
    </div>
  );
};
